import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';

import toastr from 'toastr';
import { makeStyles } from '@mui/styles';

import createHttp from 'utils/http';

import FormField from 'widgets-v5/form-field';
import Spacer from 'widgets-v5/spacer';

import TextField from '@mui/material/TextField';
import CenterLayout from 'widgets-v5/center-layout';
import { PrimaryButton, ButtonGroup } from 'widgets-v5/buttons';
import Card from 'widgets-v5/card';
import { BlockLoadGroup } from 'widgets-v5/load-group';

const http = createHttp({ redirectOnFail: false });

const useStyles = makeStyles(() => ({
    cardFooterButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginLeft: 0,
    },
}));

const Login = () => {
    const classes = useStyles();
    const emailField = useRef();
    const [message, setMessage] = React.useState('');
    const [noEmail, setNoEmail] = React.useState(true);
    const [showError, setShowError] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [resend, setResend] = React.useState(false);
    const [retryTimer, setRetryTimer] = React.useState(0);

    const emailLogin = email => {
        return http.post('emailLogin', { email });
    };

    const startRetryTimer = () => {
        setRetryTimer(60); // Set the countdown to 60 seconds
        const interval = setInterval(() => {
            setRetryTimer(prev => {
                if (prev <= 1) {
                    clearInterval(interval); // Clear the interval when the countdown reaches 0
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleEmailLogin = e => {
        e.preventDefault();

        const email = emailField.current.value;

        if (!email) {
            setShowError(true);
            setNoEmail(!email);
            return;
        }

        setIsLoading(true);
        setNoEmail(false);

        emailLogin(email).then(
            () => {
                setIsLoading(false);
                setShowError(false);
                setMessage(
                    'A sign-in link has been sent to your e-mail. Please check your inbox. \nIf you have not received it after 1 minute, you may request a new link.'
                );
                toastr.success(`An email containing the login link has been sent to ${email}.`, {
                    timeOut: 30000, // Display this message for 30 seconds
                    extendedTimeOut: 10000, // Additional time if hovered
                    closeButton: true, // Show a close button
                });
                setResend(true);
                startRetryTimer(); // Start the retry timer after a successful email login
            },
            err => {
                setIsLoading(false);
                console.log({ err });
                if (err.code === 405) {
                    browserHistory.push({
                        pathname: '/password-login',
                        state: { email }, // Pass the email in the state object
                    });
                    return;
                }
                if (err.code === 404) {
                    setShowError(true);
                    setMessage(
                        'E-mail address not found. If you already have an account, please contact us at help@engagefront.com'
                    );
                    return;
                } else if (err.code === 429) {
                    const retryAfterDate = new Date(err.body?.retryAfter);
                    const now = new Date();
                    const secondsRemaining = Math.ceil((retryAfterDate - now) / 1000);

                    setShowError(true);
                    setMessage(
                        `Too many requests. Please try again after ${secondsRemaining} seconds.`
                    );
                    return;
                } else {
                    toastr.error('An error has occurred, please try again.');
                }
            }
        );
    };

    return (
        <CenterLayout className="ef5-login">
            <div>
                <img className="ef5-login__logo" src="/images/EngageFront-logo.png" />
                <BlockLoadGroup isLoading={isLoading} size="large">
                    <form>
                        <Card
                            className="ef5-login__card"
                            title="Sign In"
                            footer={
                                <ButtonGroup className={resend ? classes.cardFooterButtons : null}>
                                    {resend && (
                                        <PrimaryButton
                                            text={'Back'}
                                            onClick={() => {
                                                // Reset state
                                                setResend(false);
                                                setMessage('');
                                                setShowError(false);
                                                setNoEmail(true);
                                                setRetryTimer(0);
                                                setIsLoading(false);
                                                emailField.current.value = ''; // Clear the email field
                                            }}
                                        />
                                    )}
                                    <PrimaryButton
                                        efEvent="LOGIN__SIGN_IN"
                                        text={
                                            retryTimer > 0
                                                ? `Resend Link (Wait ${retryTimer}s)`
                                                : resend
                                                ? 'Resend Link'
                                                : 'Next'
                                        }
                                        onClick={handleEmailLogin}
                                        disabled={retryTimer > 0} // Disable the button during the countdown
                                    />
                                </ButtonGroup>
                            }
                        >
                            <FormField
                                label="E-mail"
                                isRequired={true}
                                showErrors={showError}
                                errors={noEmail ? ['E-mail is required'] : null}
                            >
                                <TextField
                                    inputRef={emailField}
                                    fullWidth
                                    variant="outlined"
                                    type="email"
                                    autoComplete="username"
                                />
                            </FormField>
                            <Spacer />
                            <FormField showErrors={showError} errors={[message]} />
                            {message && !showError && (
                                <div style={{ whiteSpace: 'pre-line', lineHeight: '16px' }}>
                                    {message}
                                </div>
                            ) // Apply new line: pre-line
                            }
                        </Card>
                    </form>
                </BlockLoadGroup>
            </div>
        </CenterLayout>
    );
};

function select(state) {
    const o = _.get(state, 'profile', {});
    return o;
}

export default connect(select)(Login);
